import React from "react";
import Popup from "reactjs-popup";

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
    <path d="M8 4.5V8L10.5 9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const popupContentStyle = {
  border: "1px solid var(--sl-border-secondary, #e9eaeb)",
  borderRadius: "12px",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)",
  width: "480px",
  maxHeight: "360px",
  overflow: "hidden",
  padding: 0,
  background: "var(--sl-bg-primary, #fff)",
};

const extractInitials = (message) => {
  const match = message.match(/^([A-Z])[a-z]* ([A-Z])/);
  return match ? `${match[1]}${match[2]}` : "??";
};

const extractActorName = (message) => {
  const match = message.match(/^(\S+ \S+)/);
  return match ? match[1] : "System";
};

const extractAction = (message) => {
  const match = message.match(/^\S+ \S+ (.+)/);
  return match ? match[1] : message;
};

const SettingsChangelog = ({ lastEditedAt, changelogVersions }) => {
  if (!lastEditedAt || !changelogVersions || changelogVersions.length === 0) return null;

  return (
    <Popup
      arrow={false}
      offsetY={8}
      position="bottom right"
      contentStyle={popupContentStyle}
      on="hover"
      mouseEnterDelay={150}
      mouseLeaveDelay={200}
      trigger={(open) => (
        <button
          type="button"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 12px",
            fontSize: 13,
            fontWeight: 500,
            color: open ? "var(--sl-text-brand, #136395)" : "var(--sl-text-placeholder, #717680)",
            background: open ? "rgba(25, 130, 196, 0.06)" : "transparent",
            border: "1px solid",
            borderColor: open ? "rgba(25, 130, 196, 0.2)" : "var(--sl-border-secondary, #e9eaeb)",
            borderRadius: 8,
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "all 0.15s ease",
            lineHeight: 1.4,
          }}
          onMouseEnter={(e) => {
            if (!open) {
              e.currentTarget.style.borderColor = "var(--sl-border-primary, #d5d7da)";
              e.currentTarget.style.color = "var(--sl-text-tertiary, #535862)";
            }
          }}
          onMouseLeave={(e) => {
            if (!open) {
              e.currentTarget.style.borderColor = "var(--sl-border-secondary, #e9eaeb)";
              e.currentTarget.style.color = "var(--sl-text-placeholder, #717680)";
            }
          }}
        >
          <ClockIcon />
          Edited {lastEditedAt}
        </button>
      )}
    >
      {(close) => (
        <div>
          <div style={{
            padding: "16px 20px 12px",
            borderBottom: "1px solid var(--sl-border-secondary, #e9eaeb)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--sl-text-primary, #181d27)", letterSpacing: "-0.01em" }}>
              Activity
            </span>
            <span style={{ fontSize: 12, color: "var(--sl-text-quaternary, #a4a7ae)", fontWeight: 500 }}>
              {changelogVersions.length} {changelogVersions.length === 1 ? "change" : "changes"}
            </span>
          </div>

          <div style={{ maxHeight: 300, overflowY: "auto", padding: "8px 0" }}>
            {changelogVersions.map((version, index) => {
              const initials = extractInitials(version.message);
              const actor = extractActorName(version.message);
              const action = extractAction(version.message);
              const isLast = index === changelogVersions.length - 1;

              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: "10px 20px",
                    position: "relative",
                  }}
                >
                  {/* Timeline connector */}
                  {!isLast && (
                    <div style={{
                      position: "absolute",
                      left: 35,
                      top: 40,
                      bottom: -2,
                      width: 1,
                      background: "var(--sl-border-secondary, #e9eaeb)",
                    }} />
                  )}

                  {/* Avatar */}
                  <div style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #1982C4 0%, #136395 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--sl-text-white, #fff)",
                    flexShrink: 0,
                    letterSpacing: "0.02em",
                    position: "relative",
                    zIndex: 1,
                  }}>
                    {initials}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
                    <div style={{ fontSize: 13, color: "var(--sl-text-secondary, #414651)", lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 600, color: "var(--sl-text-primary, #181d27)" }}>{actor}</span>
                      {" "}
                      <span>{action}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--sl-text-quaternary, #a4a7ae)", marginTop: 2, fontWeight: 500 }}>
                      {version.date} at {version.time}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Popup>
  );
};

export default SettingsChangelog;
